import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Books', path: '/books' },
    { name: 'Audiobooks', path: '/audiobooks' },
    { name: 'Feedback', path: '/feedback' },
    { name: 'Contact', path: '/contact' },
  ];

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const isActive = (path: string) => location.pathname === path;
  const isNonHeroPage = location.pathname !== '/';
  const solidBg = isScrolled || isNonHeroPage || mobileOpen;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        solidBg ? 'bg-white shadow-sm' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between h-16">

          {/* Logo — always left */}
          <Link to="/" className="flex items-center gap-3 cursor-pointer flex-shrink-0">
            <img
              src="https://public.readdy.ai/ai/img_res/52abf2f2-316b-4670-aeb3-1738417e7b43.png"
              alt="Pastor Don R. Vining"
              className="h-10 w-10 object-contain"
            />
            <span
              className={`text-sm font-serif font-semibold whitespace-nowrap transition-colors ${
                solidBg ? 'text-[#1B3A52]' : 'text-white'
              }`}
            >
              Pastor Don R. Vining
            </span>
          </Link>

          {/* Desktop nav links */}
          <div className="hidden md:flex items-center gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  isActive(link.path)
                    ? 'text-[#D4AF37]'
                    : solidBg
                    ? 'text-[#1B3A52] hover:text-[#D4AF37]'
                    : 'text-white hover:text-[#D4AF37]'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* Mobile hamburger button */}
          <button
            className={`md:hidden flex flex-col justify-center items-center w-8 h-8 gap-1.5 cursor-pointer`}
            onClick={() => setMobileOpen((prev) => !prev)}
            aria-label="Toggle menu"
          >
            <span
              className={`block h-0.5 w-6 transition-all duration-300 ${
                solidBg ? 'bg-[#1B3A52]' : 'bg-white'
              } ${mobileOpen ? 'rotate-45 translate-y-2' : ''}`}
            />
            <span
              className={`block h-0.5 w-6 transition-all duration-300 ${
                solidBg ? 'bg-[#1B3A52]' : 'bg-white'
              } ${mobileOpen ? 'opacity-0' : ''}`}
            />
            <span
              className={`block h-0.5 w-6 transition-all duration-300 ${
                solidBg ? 'bg-[#1B3A52]' : 'bg-white'
              } ${mobileOpen ? '-rotate-45 -translate-y-2' : ''}`}
            />
          </button>
        </div>
      </div>

      {/* Mobile dropdown menu */}
      <div
        className={`md:hidden bg-white overflow-hidden transition-all duration-300 ${
          mobileOpen ? 'max-h-96 border-t border-gray-100' : 'max-h-0'
        }`}
      >
        <div className="px-6 py-4 flex flex-col gap-1">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`py-3 text-sm font-medium border-b border-gray-50 last:border-0 transition-colors cursor-pointer ${
                isActive(link.path)
                  ? 'text-[#D4AF37]'
                  : 'text-[#1B3A52] hover:text-[#D4AF37]'
              }`}
            >
              {link.name}
            </Link>
          ))}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
